import type { Todo } from "@ronin/contracts";
import { isOverdue } from "@ronin/core";
import { Button, Kbd } from "@ronin/ui";
import { Loader2, Plus, SquareCheckBig } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { NeedsConnection } from "../components/needs-connection.js";
import { useTimeZone } from "../components/settings/use-settings.js";
import { TodoDialog, toInput } from "../components/todos/todo-dialog.js";
import { TodoItem } from "../components/todos/todo-item.js";
import { useTodos } from "../components/todos/use-todos.js";
import { needsConnection } from "../lib/errors.js";
import { isTyping } from "../lib/keyboard.js";

type DialogState = { mode: "closed" } | { mode: "create" } | { mode: "edit"; todo: Todo };

export function TodosPage() {
  const actions = useTodos();
  const { data: todos = [], isPending, isError } = actions.query;
  const offline = needsConnection(actions.query);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const timeZone = useTimeZone();

  // "N" opens the add dialog (spec01 FR9).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "n" || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTyping(event) || document.querySelector("dialog[open]")) return;
      event.preventDefault();
      setDialog({ mode: "create" });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const open = todos.filter((t) => !t.done);
  const done = todos.filter((t) => t.done);
  // FR7: important open todos get their own section above the others (already sorted).
  const important = open.filter((t) => t.important);
  const normal = open.filter((t) => !t.important);
  const overdue = open.filter((t) => isOverdue(t, new Date(), timeZone)).length;
  const close = () => setDialog({ mode: "closed" });

  const rowActions = {
    onOpen: (todo: Todo) => setDialog({ mode: "edit", todo }),
    onToggleDone: (todo: Todo) =>
      actions.update.mutate({ id: todo.id, patch: { done: !todo.done } }),
    onToggleImportant: (todo: Todo) =>
      actions.update.mutate({ id: todo.id, patch: { important: !todo.important } }),
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold text-foreground">Todos</h1>
          {!isPending && !isError && (
            <p className="text-xs text-muted-foreground tabular-nums">
              {open.length} open
              {overdue > 0 && <span className="text-destructive"> · {overdue} overdue</span>}
              {done.length > 0 && <> · {done.length} done</>}
            </p>
          )}
        </div>
        <Button onClick={() => setDialog({ mode: "create" })}>
          <Plus />
          New todo
          <Kbd className="hidden sm:inline-flex">N</Kbd>
        </Button>
      </header>

      {offline && <NeedsConnection onRetry={() => void actions.query.refetch()} />}
      {isPending && !offline && (
        <p className="flex items-center gap-2 py-12 text-sm text-dim-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading todos…
        </p>
      )}
      {isError && !offline && (
        <p className="border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load your todos. Try reloading the page.
        </p>
      )}

      {!isPending && !isError && todos.length === 0 && (
        <div className="flex flex-col items-center gap-3 border border-dashed border-input px-6 py-16 text-center">
          <SquareCheckBig className="size-6 text-brand" aria-hidden />
          <h2 className="text-sm font-semibold text-foreground">No todos yet</h2>
          <p className="text-xs text-muted-foreground">Add the first thing you want to get done.</p>
          <Button className="mt-2" onClick={() => setDialog({ mode: "create" })}>
            <Plus />
            New todo
          </Button>
        </div>
      )}

      {important.length > 0 && (
        <Section id="important-heading" label="Important" count={important.length}>
          <ul aria-label="Important todos" className="flex flex-col gap-2">
            {important.map((todo) => (
              <TodoItem key={todo.id} todo={todo} {...rowActions} />
            ))}
          </ul>
        </Section>
      )}
      {normal.length > 0 && (
        <Section id="open-heading" label="To do" count={normal.length}>
          <ul aria-label="Open todos" className="flex flex-col gap-2">
            {normal.map((todo) => (
              <TodoItem key={todo.id} todo={todo} {...rowActions} />
            ))}
          </ul>
        </Section>
      )}
      {!isPending && !isError && open.length === 0 && done.length > 0 && (
        <p className="px-1 text-xs text-muted-foreground">All done. Nothing open right now.</p>
      )}

      {done.length > 0 && (
        <Section id="done-heading" label="Done" count={done.length} divider>
          <ul aria-label="Done todos" className="flex flex-col gap-2">
            {done.map((todo) => (
              <TodoItem key={todo.id} todo={todo} {...rowActions} />
            ))}
          </ul>
        </Section>
      )}

      <TodoDialog
        open={dialog.mode !== "closed"}
        todo={dialog.mode === "edit" ? dialog.todo : undefined}
        onOpenChange={(isOpen) => !isOpen && close()}
        onSubmit={async (values) => {
          if (dialog.mode === "edit") {
            await actions.update.mutateAsync({ id: dialog.todo.id, patch: toInput(values) });
          } else {
            await actions.create.mutateAsync(toInput(values));
          }
          close();
        }}
        onDelete={(todo) => {
          close();
          actions.remove.mutate(todo);
        }}
      />
    </div>
  );
}

/** List section: small uppercase label with count; `divider` adds a hairline after it. */
function Section({
  id,
  label,
  count,
  divider,
  children,
}: {
  id: string;
  label: string;
  count: number;
  divider?: boolean;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <div className="flex items-center gap-3 px-1">
        <h2
          id={id}
          className="flex items-baseline gap-1.5 text-xs font-semibold uppercase tracking-wide text-dim-foreground"
        >
          {label}
          <span className="font-normal normal-case text-faint-foreground tabular-nums">
            {count}
          </span>
        </h2>
        {divider && <span className="h-px flex-1 bg-border" aria-hidden />}
      </div>
      {children}
    </section>
  );
}
